import React from "react";
import { Link } from "components";
import { useAllMenus } from "hooks";
import { MenuLocationEnum, flatListToHierarchical } from "utils";
import "./Menu.scss";
import classNames from "classnames";

interface MenuInterface {
  className?: string;
  menuLocation: keyof typeof MenuLocationEnum;
}

/**
 * We can get all of our menu locations within here and return the correct menu based off our props.
 *
 * @param menuLocation Enum value for the menu location
 * @param className Optional if an additional class is required.
 * @returns Menu items from a desired location
 *
 */
export const Menu = ({ menuLocation = "PRIMARY_NAVIGATION", className }: MenuInterface): JSX.Element => {
  /* First we retrieve our menu */
  const filteredMenu = useAllMenus(menuLocation);
  /* We then need to convert the menu into a hierarchical list */
  const hierarchicalMenu = flatListToHierarchical(filteredMenu?.menuItems?.nodes);

  return (
    <nav>
      <ul className={`menu ${className}`}>
        {hierarchicalMenu?.map((menuItem) => (
          <MenuItem key={menuItem.id} className={className} {...menuItem} />
        ))}
      </ul>
    </nav>
  );
};

const menuItemTierEnglishNames = {
  1: `parent`,
  2: `child`,
  3: `grandchild`,
  4: `great-grandchild`,
};

interface MenuItemInterface {
  cssClasses?: string;
  url?: string;
  path?: string;
  label?: string;
  className?: string;
  tier?: number;
  childItems?: Record<string, any>[]; // replace this with the correct type
}

/**
 * MenuItem renders a menu item for a given menu. This is a recursive component that calls itself when
 * there is a childItem present.
 * You can use the data-nested-tier or data-tier-name to control styles for the child elements.
 * @param {string} cssClasses - Any custom classes provided from WordPress
 * @param {string} url - The link url
 * @param {string} label - The link label
 * @param {Object[]} childItems - Link children
 * @param {number} tier - What tier/level our menu item sits at
 * @returns JSXElement
 */
const MenuItem = ({
  cssClasses,
  url,
  path,
  label,
  childItems = [],
  className = "menu-item",
  tier = 1, // tracks which tier the menu item is
}: MenuItemInterface) => {
  const hasChildren = childItems?.length > 0 ? true : false;
  return (
    <div
      className={classNames({
        [`${className}-item__wrapper`]: className,
        [`${className}-item__parent}`]: hasChildren,
      })}
      data-nested-tier={tier}
      data-tier-name={menuItemTierEnglishNames[tier]}
    >
      {!hasChildren ? (
        <li className={`${className ? `${className}__item ` : ""}${cssClasses}`}>
          <Link to={path}>{label}</Link>
        </li>
      ) : (
        <>
          <li className={`${className ? `${className}__item ` : ""}${cssClasses}`}>
            <Link to={path}>{label}</Link>
          </li>
          <ul className={`${className ? `${className}__item__children__menu` : ""}${cssClasses}`}>
            {childItems?.map((childItem) => (
              <MenuItem key={childItem?.id} className={className} {...childItem} tier={tier + 1} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
